import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { ordersAPI, paymentAPI, productsAPI } from '../services/api';
import { Product } from '../types';
import { getAvailableStock } from '../utils/stock';
import { checkCategoryMinimums, getDeliveryFee } from '../utils/delivery';
import './Cart.css';

interface GuestInfo {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  streetAddress: string;
  city: string;
  state: string;
  zipcode: string;
  deliveryNotes: string;
}

// Delivery covers the Metro St. Louis area, which spans both sides of the river.
const DELIVERY_STATES = [
  { code: 'MO', name: 'Missouri' },
  { code: 'IL', name: 'Illinois' }
];

const CheckoutForm: React.FC<{
  onSuccess: () => void,
  categories: any[],
  settings: {tax_rate: number, delivery_fee: number},
  // Passed down rather than recalculated, so the payment modal can never quote
  // a different total than the cart summary the customer just agreed to.
  deliveryFee: number,
  meetsMinimums: boolean
}> = ({ onSuccess, categories, settings, deliveryFee, meetsMinimums }) => {
  const { items, getTotalAmount, clearCart } = useCart();
  const { user } = useAuth();
  const [guestInfo, setGuestInfo] = useState<GuestInfo>({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    streetAddress: '',
    city: '',
    state: 'MO',
    zipcode: '',
    deliveryNotes: ''
  });

  // Initialize address fields with user data when component mounts
  React.useEffect(() => {
    if (user && user.address) {
      const addressParts = user.address.split(',');
      const street = addressParts[0]?.trim() || '';
      const cityPart = addressParts[1]?.trim() || '';
      const zipPart = user.address.split(' ').pop() || '';
      
      setGuestInfo(prev => ({
        ...prev,
        streetAddress: street,
        city: cityPart,
        zipcode: zipPart
      }));
    }
  }, [user]);
  const [processing, setProcessing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'venmo' | 'delivery'>('venmo');
  const [paymentAcknowledged, setPaymentAcknowledged] = useState(false);
  const [orderConfirmation, setOrderConfirmation] = useState<{orderNumber: string, total: number} | null>(null);
  const [notification, setNotification] = useState<{message: string, type: 'error' | 'success'} | null>(null);

  const validateForm = () => {
    const errors = [];
    
    if (!user) {
      if (!guestInfo.firstName.trim()) errors.push('First name is required');
      if (!guestInfo.lastName.trim()) errors.push('Last name is required');
      if (!guestInfo.email.trim()) errors.push('Email is required');
      if (!guestInfo.phone.trim()) errors.push('Phone number is required');
    }
    
    const streetAddress = guestInfo.streetAddress;
    const city = guestInfo.city;
    const zipcode = guestInfo.zipcode;
    
    if (!streetAddress?.trim()) errors.push('Street address is required');
    if (!city?.trim()) errors.push('City is required');
    if (!zipcode?.trim()) errors.push('ZIP code is required');
    
    return errors;
  };

  const showNotification = (message: string, type: 'error' | 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  // Order total, computed once and reused everywhere below. Previously each of
  // the seven places that needed it recalculated the same expression inline,
  // which made it easy for one of them to drift.
  const subtotal = getTotalAmount();
  const taxAmount = subtotal * settings.tax_rate / 100;
  const orderTotal = subtotal + taxAmount + deliveryFee;

  const handleProceedToPayment = () => {
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      showNotification('Please fill in all required fields: ' + validationErrors.join(', '), 'error');
      return;
    }

    // Category minimums no longer block checkout. Falling short simply means the
    // home delivery fee applies, which is shown in the summary and again below.
    setShowPaymentModal(true);
  };

  const handlePlaceOrder = async () => {
    if (!paymentAcknowledged) return;
    
    setProcessing(true);

    try {
      // Always use form values from guestInfo (which are initialized with user data if available)
      const streetAddress = guestInfo.streetAddress;
      const city = guestInfo.city;
      const state = guestInfo.state;
      const zipcode = guestInfo.zipcode;
      
      const orderData = {
        items: items.map(item => ({
          productId: item.productId,
          quantity: item.quantity,
          price: item.price,
          category: item.category
        })),
        deliveryAddress: `${streetAddress}, ${city}, ${state} ${zipcode}`,
        streetAddress,
        city,
        state,
        zipcode,
        totalAmount: orderTotal,
        isPaid: paymentMethod === 'venmo',
        deliveryNotes: guestInfo.deliveryNotes,
        guestInfo: user ? null : guestInfo
      };

      const response = await ordersAPI.create(orderData);
      const orderNumber = response.data.orderNumber || 'TFF001000';

      console.log('Order created successfully, showing confirmation');
      setShowPaymentModal(false);
      
      // Show confirmation immediately - don't clear cart yet
      setOrderConfirmation({
        orderNumber,
        total: orderTotal
      });
      console.log('Order confirmation set:', { orderNumber, total: orderTotal });
    } catch (error: any) {
      console.error('Order placement failed:', error);
      showNotification(error.response?.data?.message || 'Order failed', 'error');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <form className="checkout-form">
        {!user && (
          <div className="guest-info">
            <h3>Contact Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={guestInfo.firstName}
                  onChange={(e) => setGuestInfo({...guestInfo, firstName: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={guestInfo.lastName}
                  onChange={(e) => setGuestInfo({...guestInfo, lastName: e.target.value})}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Email Address *</label>
              <input
                type="email"
                value={guestInfo.email}
                onChange={(e) => setGuestInfo({...guestInfo, email: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>Phone Number *</label>
              <input
                type="tel"
                value={guestInfo.phone}
                onChange={(e) => setGuestInfo({...guestInfo, phone: e.target.value})}
                required
              />
            </div>
          </div>
        )}
        
        <div className="address-section">
          <h3>Delivery Address</h3>
          <div className="form-group">
            <label>Street Address *</label>
            <input
              type="text"
              value={guestInfo.streetAddress}
              onChange={(e) => setGuestInfo({...guestInfo, streetAddress: e.target.value})}
              required
              placeholder="123 Main Street"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>City *</label>
              <input
                type="text"
                value={guestInfo.city}
                onChange={(e) => setGuestInfo({...guestInfo, city: e.target.value})}
                required
                placeholder="St. Louis"
              />
            </div>
            <div className="form-group">
              <label htmlFor="delivery-state">State *</label>
              <select
                id="delivery-state"
                value={guestInfo.state}
                onChange={(e) => setGuestInfo({...guestInfo, state: e.target.value})}
                className="state-select"
                required
              >
                {DELIVERY_STATES.map(({ code, name }) => (
                  <option key={code} value={code}>{name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>ZIP Code *</label>
              <input
                type="text"
                value={guestInfo.zipcode}
                onChange={(e) => setGuestInfo({...guestInfo, zipcode: e.target.value})}
                required
                placeholder="63017"
                pattern="[0-9]{5}(-[0-9]{4})?"
              />
            </div>
          </div>
        </div>
        
        <div className="delivery-notes-section">
          <h3>Delivery Notes (Optional)</h3>
          <div className="form-group">
            <label>Special Instructions</label>
            <textarea
              value={guestInfo.deliveryNotes}
              onChange={(e) => {
                if (e.target.value.length <= 500) {
                  setGuestInfo({...guestInfo, deliveryNotes: e.target.value});
                }
              }}
              placeholder="Any special delivery instructions? (e.g., gate code, preferred delivery time, location preferences)"
              className="delivery-notes-textarea"
              rows={4}
            />
            <div className="character-count">
              {guestInfo.deliveryNotes.length}/500 characters
            </div>
          </div>
        </div>
        
        {meetsMinimums ? (
          <div className="delivery-perk checkout">
            <span className="delivery-perk-icon">🚚</span>
            <span>
              <strong>Free home delivery</strong> included &mdash; you met the order minimum.
            </span>
          </div>
        ) : (
          <div className="delivery-fee-notice checkout">
            <span className="delivery-fee-icon">🚚</span>
            <span>
              A <strong>${deliveryFee.toFixed(2)} home delivery fee</strong> is included in this
              total because your order is below the minimum. Add more to your cart to get
              delivery free.
            </span>
          </div>
        )}

        <button type="button" onClick={handleProceedToPayment} className="pay-button">
          Proceed to Payment - ${orderTotal.toFixed(2)}
        </button>
      </form>

      {showPaymentModal && (
        <div className="payment-modal-overlay">
          <div className="payment-modal">
            <h3>Payment Options</h3>
            
            <div className="payment-methods">
              <button 
                className={`payment-method-btn ${paymentMethod === 'venmo' ? 'active' : ''}`}
                onClick={() => { setPaymentMethod('venmo'); setPaymentAcknowledged(false); }}
              >
                Pay with Venmo
              </button>
              <button 
                className={`payment-method-btn ${paymentMethod === 'delivery' ? 'active' : ''}`}
                onClick={() => { setPaymentMethod('delivery'); setPaymentAcknowledged(false); }}
              >
                Pay on Delivery
              </button>
            </div>

            {paymentMethod === 'venmo' && (
              <div className="venmo-payment">
                <div className="venmo-qr">
                  <img src="/images/tff venmo.png" alt="Venmo QR Code" />
                </div>
                <p className="venmo-text">
                  Send <strong>${orderTotal.toFixed(2)}</strong> to <strong>@tony-white-04033</strong>
                </p>
                <p className="payment-instruction" style={{color: 'var(--primary-green)', fontWeight: 'bold', marginBottom: '10px'}}>
                  Check this box once you have sent the payment:
                </p>
                <label className="payment-acknowledgment">
                  <input 
                    type="checkbox" 
                    checked={paymentAcknowledged}
                    onChange={(e) => setPaymentAcknowledged(e.target.checked)}
                  />
                  I sent ${orderTotal.toFixed(2)} to @tony-white-04033 on Venmo
                </label>
              </div>
            )}

            {paymentMethod === 'delivery' && (
              <div className="delivery-payment">
                <p className="delivery-text">
                  You will pay <strong>${orderTotal.toFixed(2)}</strong> on the day of delivery
                </p>
                <label className="payment-acknowledgment">
                  <input 
                    type="checkbox" 
                    checked={paymentAcknowledged}
                    onChange={(e) => setPaymentAcknowledged(e.target.checked)}
                  />
                  I acknowledge that I have NOT YET sent ${orderTotal.toFixed(2)} to @tony-white-04033 on Venmo, and I agree to sending that amount on the day of my delivery
                </label>
              </div>
            )}

            <div className="payment-actions">
              <button 
                onClick={() => setShowPaymentModal(false)}
                className="cancel-payment"
              >
                Cancel
              </button>
              <button 
                onClick={handlePlaceOrder}
                disabled={!paymentAcknowledged || processing}
                className={`place-order-btn ${paymentAcknowledged ? 'active' : ''}`}
              >
                {processing ? 'Placing Order...' : 'Place Order'}
              </button>
            </div>
          </div>
        </div>
      )}

      {orderConfirmation && (
        <div className="confirmation-modal-overlay">
          <div className="confirmation-modal">
            <div className="confirmation-icon">✅</div>
            <h2>Order Placed Successfully!</h2>
            <div className="order-details">
              <p><strong>Order Number:</strong> {orderConfirmation.orderNumber}</p>
              <p><strong>Total:</strong> ${orderConfirmation.total.toFixed(2)}</p>
              <p className="confirmation-message">
                Thank you for your order!
              </p>
            </div>
            <button 
              onClick={() => {
                setOrderConfirmation(null);
                clearCart();
                onSuccess();
              }}
              className="continue-btn"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      )}
      {notification && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}
    </>
  );
};

const Cart: React.FC = () => {
  const { items, updateQuantity, removeItem, getTotalAmount } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showCheckout, setShowCheckout] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [settings, setSettings] = useState<{tax_rate: number, delivery_fee: number}>({tax_rate: 8.738, delivery_fee: 15.00});
  const [products, setProducts] = useState<Product[]>([]);

  React.useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch('/api/categories');
        const data = await response.json();
        setCategories(data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    
    const fetchSettings = async () => {
      try {
        const response = await fetch('/api/settings');
        const data = await response.json();
        setSettings(data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    
    const fetchProducts = async () => {
      try {
        const response = await productsAPI.getAll();
        setProducts(response.data);
      } catch (error) {
        // Non-fatal: without this the cart simply does not show stock warnings.
        // The order endpoint still enforces availability.
        console.error('Error fetching products for stock check:', error);
      }
    };

    fetchCategories();
    fetchSettings();
    fetchProducts();
  }, []);

  // Live stock, so a cart left open while a product sold out is caught here
  // rather than at the very end of checkout.
  const availableFor = (productId: number): number | null => {
    const product = products.find(p => p.id === productId);
    if (!product) return null;
    return getAvailableStock(product);
  };

  const stockProblems = items
    .map(item => {
      const available = availableFor(item.productId);
      if (available === null || item.quantity <= available) return null;
      return { name: item.name, requested: item.quantity, available };
    })
    .filter((problem): problem is { name: string; requested: number; available: number } =>
      problem !== null
    );

  const minimumCheck = checkCategoryMinimums(items, categories);
  const meetsMinimums = minimumCheck.meets;
  // Meeting any minimum earns free delivery; otherwise the admin-configured fee
  // applies. Nothing here blocks checkout.
  const deliveryFee = getDeliveryFee(meetsMinimums, settings.delivery_fee);

  const handleCheckout = () => {
    if (user) {
      setShowCheckout(true);
      // Auto-scroll to checkout form after state update
      setTimeout(() => {
        const checkoutSection = document.querySelector('.checkout-section');
        if (checkoutSection) {
          checkoutSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      setShowAuthModal(true);
    }
  };

  const handleScrollToCheckout = () => {
    const checkoutSection = document.querySelector('.checkout-section');
    if (checkoutSection) {
      checkoutSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleAuthChoice = (choice: 'login' | 'register' | 'guest') => {
    setShowAuthModal(false);
    if (choice === 'login') {
      navigate('/login', { state: { returnTo: '/cart' } });
    } else if (choice === 'register') {
      navigate('/register', { state: { returnTo: '/cart' } });
    } else {
      setShowCheckout(true);
    }
  };

  const handleCheckoutSuccess = () => {
    // Navigate after user dismisses confirmation
    if (user) {
      navigate('/orders');
    } else {
      navigate('/');
    }
  };

  if (items.length === 0) {
    return (
      <div className="cart-container">
        <div className="cart-header">
          <h1>Your Cart</h1>
        </div>
        <div className="empty-cart">
          <h2>Your cart is empty</h2>
          <p>Add some products to get started!</p>
          <button onClick={() => navigate('/products')} className="continue-shopping">
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-container">
      <div className="cart-header">
        <h1>Your Cart</h1>
        <button onClick={() => navigate('/products')} className="continue-shopping-link">
          Continue Shopping
        </button>
      </div>
      
      <div className="cart-content">
        <div className="cart-items">
          {items.map(item => {
            const available = availableFor(item.productId);
            const atLimit = available !== null && item.quantity >= available;
            const overLimit = available !== null && item.quantity > available;

            return (
            <div key={item.productId} className={`cart-item ${overLimit ? 'over-stock' : ''}`}>
              <div className="item-info">
                <h3>{item.name}</h3>
                <p className="item-price">${item.price.toFixed(2)} each</p>
                {overLimit && (
                  <p className="item-stock-warning">
                    {available === 0
                      ? 'Sold out since you added this. Please remove it.'
                      : `Only ${available} left. Please lower the quantity to ${available} or less.`}
                  </p>
                )}
                {!overLimit && atLimit && available !== null && available > 0 && (
                  <p className="item-stock-note">That is all we have left</p>
                )}
              </div>
              <div className="item-controls">
                <div className="quantity-controls">
                  <button 
                    onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                    className="quantity-btn"
                    aria-label={`Decrease ${item.name} quantity`}
                  >
                    -
                  </button>
                  <span className="quantity">{item.quantity}</span>
                  <button 
                    onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                    className="quantity-btn"
                    disabled={atLimit}
                    aria-label={`Increase ${item.name} quantity`}
                  >
                    +
                  </button>
                </div>
                <div className="item-total">${(item.price * item.quantity).toFixed(2)}</div>
                <button 
                  onClick={() => removeItem(item.productId)} 
                  className="remove-button"
                >
                  Remove
                </button>
              </div>
            </div>
            );
          })}
        </div>

        <div className="cart-summary">
          <h3>Order Summary</h3>
          
          <div className="summary-line">
            <span>Subtotal:</span>
            <span>${getTotalAmount().toFixed(2)}</span>
          </div>
          
          <div className="summary-line">
            <span>Tax ({settings.tax_rate}%):</span>
            <span>${(getTotalAmount() * settings.tax_rate / 100).toFixed(2)}</span>
          </div>
          
          <div className={`summary-line ${meetsMinimums ? 'free-delivery' : ''}`}>
            <span>Home Delivery:</span>
            {meetsMinimums ? (
              <span className="free-delivery-value">FREE</span>
            ) : (
              <span>${deliveryFee.toFixed(2)}</span>
            )}
          </div>
          
          <div className="summary-line total">
            <span>Total:</span>
            <span>${(getTotalAmount() + (getTotalAmount() * settings.tax_rate / 100) + deliveryFee).toFixed(2)}</span>
          </div>

          {meetsMinimums ? (
            <div className="delivery-perk">
              <div className="delivery-perk-icon">🎉</div>
              <div className="delivery-perk-text">
                <strong>You unlocked free home delivery!</strong>
                <p>Your order meets the minimum, so we have waived the delivery fee.</p>
              </div>
            </div>
          ) : (
            <div className="delivery-fee-notice">
              <div className="delivery-fee-icon">🚚</div>
              <div className="delivery-fee-text">
                <strong>${deliveryFee.toFixed(2)} home delivery fee applies</strong>
                <p>
                  {minimumCheck.shortfall && minimumCheck.category ? (
                    <>
                      Add <strong>{minimumCheck.shortfall} more {minimumCheck.category.toLowerCase()}</strong>
                      {' '}({minimumCheck.required} total) and delivery is <strong>free</strong>.
                    </>
                  ) : (
                    <>Meet any product minimum and delivery is <strong>free</strong>.</>
                  )}
                </p>
                <p className="delivery-fee-reassure">
                  You can still check out now &mdash; the fee is already included in your total.
                </p>
              </div>
            </div>
          )}

          {stockProblems.length > 0 && (
            <div className="stock-warning">
              <p><strong>Not enough stock for some items:</strong></p>
              <ul>
                {stockProblems.map(problem => (
                  <li key={problem.name}>
                    {problem.name}: {problem.requested} requested,{' '}
                    {problem.available === 0 ? 'sold out' : `${problem.available} available`}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Being under a minimum no longer blocks checkout; only a genuine
              stock shortage does. */}
          <button 
            onClick={showCheckout ? handleScrollToCheckout : handleCheckout} 
            className={`checkout-button ${stockProblems.length > 0 ? 'disabled' : ''}`}
            disabled={stockProblems.length > 0}
          >
            {showCheckout ? 'Checkout Form Below' : 'Proceed to Checkout'}
          </button>
        </div>
      </div>

      {showAuthModal && (
        <div className="auth-modal-overlay">
          <div className="auth-modal">
            <h3>Choose how to continue</h3>
            <p>To complete your order, please choose one of the options below:</p>
            <div className="auth-options">
              <button 
                onClick={() => handleAuthChoice('login')}
                className="auth-option-btn login"
              >
                Sign In
                <span>Use your existing account</span>
              </button>
              <button 
                onClick={() => handleAuthChoice('register')}
                className="auth-option-btn register"
              >
                Create Account
                <span>Save your info for faster checkout</span>
              </button>
              <button 
                onClick={() => handleAuthChoice('guest')}
                className="auth-option-btn guest"
              >
                Continue as Guest
                <span>No account needed</span>
              </button>
            </div>
            <button 
              onClick={() => setShowAuthModal(false)}
              className="close-modal"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {showCheckout && (
        <div className="checkout-section">
          <h2>Checkout</h2>
          <CheckoutForm
            onSuccess={handleCheckoutSuccess}
            categories={categories}
            settings={settings}
            deliveryFee={deliveryFee}
            meetsMinimums={meetsMinimums}
          />
        </div>
      )}
    </div>
  );
};

export default Cart;